import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AccountBalanceIcon,
  AddCircleIcon,
  CalendarIcon,
  ChatIcon,
  ChevronRightIcon,
  EditIcon,
  EditLocationIcon,
  ForumIcon,
  GridViewIcon,
  GroupsIcon,
  InfoIcon,
  LocationIcon,
  MarketIcon,
  MenuBookIcon,
  NewspaperIcon,
  NotificationsIcon,
  PersonIcon,
  PodcastsIcon,
  PersonAddIcon,
  StarIcon,
  SuggestionIcon,
  TreesIcon
} from '@/assets';
import {
  RATE_APP_ANDROID_URL,
  RATE_APP_IOS_URL,
  RATE_APP_URL
} from '@/features/tutorial/constants/links';
import type { TourAction, TourPage } from '@/features/tutorial/types/tutorial';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { TabRoute } from '@/navigation/types/navigation';
import { isWeb } from '@/shared/utils/platform';
import { handleOpenUrl } from '@/shared/utils/url';

/**
 * The tour pages. Labels of the real tabs and sheets are taken from their own
 * namespaces so the tour and the app never disagree on naming.
 */
export const usePages = (): TourPage[] => {
  const { t } = useTranslation(LocalizationNameSpace.TUTORIAL, {
    keyPrefix: 'page'
  });
  const { t: tTabs } = useTranslation(LocalizationNameSpace.NAVIGATION, {
    keyPrefix: 'tabs'
  });
  const { t: tRegion } = useTranslation(LocalizationNameSpace.GEO_AREA, {
    keyPrefix: 'components.regionPartnersSheet'
  });

  return useMemo(
    (): TourPage[] => [
      {
        key: 'start',
        tab: TabRoute.Start,
        title: t('start.title'),
        summary: t('start.summary'),
        features: [
          { icon: NewspaperIcon, label: t('start.features.posts') },
          { icon: TreesIcon, label: t('start.features.region') },
          { icon: CalendarIcon, label: t('start.features.events') },
          { icon: InfoIcon, label: t('start.features.hints') }
        ],
        mock: {
          header: {
            icon: LocationIcon,
            title: t('start.mock.header'),
            expandable: true
          },
          rows: [NewspaperIcon, CalendarIcon, TreesIcon]
        }
      },
      {
        key: 'funkzentrale',
        tab: TabRoute.Radio,
        title: t('funkzentrale.title'),
        summary: t('funkzentrale.summary'),
        features: [
          { icon: NewspaperIcon, label: t('funkzentrale.features.news') },
          { icon: ForumIcon, label: t('funkzentrale.features.plausch') },
          { icon: MarketIcon, label: t('funkzentrale.features.markt') },
          { icon: CalendarIcon, label: t('funkzentrale.features.events') },
          { icon: AddCircleIcon, label: t('funkzentrale.features.create') }
        ],
        mock: {
          header: { icon: LocationIcon, title: tTabs(TabRoute.Radio) },
          tabs: [
            GridViewIcon,
            NewspaperIcon,
            ForumIcon,
            MarketIcon,
            CalendarIcon
          ],
          rows: [NewspaperIcon, MarketIcon],
          fab: true,
          highlight: 'fab'
        }
      },
      {
        key: 'gruppenInfos',
        tab: TabRoute.Radio,
        title: t('gruppenInfos.title'),
        summary: t('gruppenInfos.summary'),
        features: [
          { icon: GroupsIcon, label: t('gruppenInfos.features.groups') },
          { icon: ChatIcon, label: t('gruppenInfos.features.chats') },
          { icon: SuggestionIcon, label: t('gruppenInfos.features.sagsUns') },
          { icon: AccountBalanceIcon, label: t('gruppenInfos.features.infos') }
        ],
        mock: {
          header: { icon: LocationIcon, title: tTabs(TabRoute.Radio) },
          tabs: [NewspaperIcon, ChatIcon, AccountBalanceIcon],
          activeTab: 1,
          rows: [GroupsIcon, ChatIcon],
          highlight: 'tabs'
        }
      },
      {
        key: 'region',
        tab: TabRoute.Region,
        title: t('region.title'),
        summary: t('region.summary'),
        features: [
          { icon: AccountBalanceIcon, label: t('region.features.ministries') },
          { icon: PodcastsIcon, label: t('region.features.regionalRadio') },
          { icon: NotificationsIcon, label: t('region.features.emergency') },
          { icon: CalendarIcon, label: t('region.features.events') }
        ],
        mock: {
          header: { icon: TreesIcon, title: t('region.mock.header') },
          tabs: [
            AccountBalanceIcon,
            PodcastsIcon,
            NotificationsIcon,
            CalendarIcon
          ],
          rows: [AccountBalanceIcon, PodcastsIcon],
          highlight: 'tabs'
        }
      },
      {
        key: 'partner',
        tab: TabRoute.Region,
        title: t('partner.title'),
        summary: t('partner.summary'),
        features: [
          { icon: TreesIcon, label: t('partner.features.open') },
          { icon: GroupsIcon, label: t('partner.features.list') },
          { icon: ChevronRightIcon, label: t('partner.features.detail') }
        ],
        hint: t('partner.hint'),
        mock: {
          header: {
            icon: TreesIcon,
            title: t('region.mock.header'),
            subtitle: t('partner.mock.subtitle'),
            expandable: true
          },
          rows: [AccountBalanceIcon],
          sheet: { title: tRegion('title'), rows: 3 },
          highlight: 'header'
        }
      },
      {
        key: 'profil',
        tab: TabRoute.Profile,
        title: t('profil.title'),
        summary: t('profil.summary'),
        features: [
          { icon: EditIcon, label: t('profil.features.edit') },
          { icon: EditLocationIcon, label: t('profil.features.radius') },
          {
            icon: NotificationsIcon,
            label: t('profil.features.notifications')
          },
          { icon: MenuBookIcon, label: t('profil.features.tour') }
        ],
        mock: {
          header: { icon: PersonIcon, title: tTabs(TabRoute.Profile) },
          rows: [EditIcon, EditLocationIcon, NotificationsIcon, MenuBookIcon]
        }
      },
      {
        key: 'rate',
        title: t('rate.title'),
        summary: t('rate.summary'),
        features: [
          { icon: StarIcon, label: t('rate.features.rate') },
          { icon: ForumIcon, label: t('rate.features.feedback') },
          { icon: PersonAddIcon, label: t('rate.features.invite') }
        ],
        mock: {
          header: { icon: StarIcon, title: t('rate.mock.header') },
          stars: 5,
          rows: [ForumIcon, PersonAddIcon]
        },
        // Native apps link to their own store; the web app offers both. Any
        // action whose URL is missing (env not provided, unsupported platform)
        // is dropped so no dead button ships.
        actions: (isWeb
          ? [
              { label: t('rate.actionIos'), url: RATE_APP_IOS_URL },
              { label: t('rate.actionAndroid'), url: RATE_APP_ANDROID_URL }
            ]
          : [{ label: t('rate.action'), url: RATE_APP_URL }]
        )
          .filter(
            (a): a is { label: string; url: string } => a.url !== undefined
          )
          .map<TourAction>(({ label, url }) => ({
            label,
            onPress: () => void handleOpenUrl(url, { native: 'system' })
          }))
      }
    ],
    [t, tTabs, tRegion]
  );
};
